import { useEffect, useRef, useState } from "react";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { ProductCards } from "../ProductCards/ProductCards";
import { useTranslation } from "react-i18next";
import { useAmplitude } from "../../../context/AmplitudeContext";
import { useUID } from "../../../context/UIDContext";
import { ProductsPerPage } from "../../../constants";
import "./ProductPage.scss";
import { ProductFilterDropdown } from "../ProductFilterDropDown/ProductFilterDropDown";

export const ProductPage = ({ results, recommendedData, onBackClick }) => {
  const { t } = useTranslation(["productModule", "aiResults"]);
  const trackEvent = useAmplitude();
  const { uid } = useUID();

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedBrand, setSelectedBrand] = useState("All-Brands");
  const [selectedCategory, setSelectedCategory] = useState("All-Categories");
  const [filteredProducts, setFilteredProducts] = useState(
    recommendedData.recommendations ?? [],
  );

  const productsRef = useRef(null);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / ProductsPerPage),
  );

  const goToPage = (page) => {
    const next = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(next);
    productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Windowed page numbers: 1 … (current-1) current (current+1) … last
  const getPageNumbers = () => {
    const pages = [];
    for (let page = 1; page <= totalPages; page++) {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        pages.push(page);
      } else if (pages[pages.length - 1] !== "…") {
        pages.push("…");
      }
    }
    return pages;
  };

  const getPaginatedProducts = () => {
    if (filteredProducts) {
      const startIndex = (currentPage - 1) * ProductsPerPage;
      const endIndex = startIndex + ProductsPerPage;
      const paginated = filteredProducts.slice(startIndex, endIndex);
      return paginated;
    }
    return [];
  };

  const paginatedProducts = getPaginatedProducts();

  useEffect(() => {
    trackEvent("webapp_user_visited_products_page", uid);
  }, [uid, trackEvent]);

  useEffect(() => {
    const filteredProducts = recommendedData.recommendations.filter(
      (product) =>
        (selectedBrand === "All-Brands" || product.brand === selectedBrand) &&
        (selectedCategory === "All-Categories" ||
          product.categories.includes(selectedCategory)),
    );
    setCurrentPage(1);
    setFilteredProducts(filteredProducts);
  }, [recommendedData.recommendations, selectedBrand, selectedCategory]);

  return (
    <div className="product-recommend">
      <div className="logo-bar">
        <div className="products-back-button-wrapper">
          <div className="back-icon" onClick={() => onBackClick()}>
            <IoChevronBackOutline />
          </div>
        </div>
        <div className="logo-bar-center-title">
          <h4 className="products-page-bar-text">{t("title-1")}</h4>
          <h4 className="products-page-bar-text">{t("title-2")}</h4>
        </div>
      </div>
      <div className="results-slider">
        {Object.entries(results ?? {}).map(([aiCategory, aiResult]) => (
          <div key={aiCategory} className="result-box">
            <h4 className="result-box-category">
              {t(aiCategory, { ns: "aiResults" })}:
            </h4>
            <h4 className="result-box-value">
              {t(aiResult.value, { ns: "aiResults" })}
            </h4>
          </div>
        ))}
      </div>
      <div className="recommendation-scroll-content">
        <p className="product-page-intro-text">{t("intro-text")}</p>
        <div className="product-page-section-header">
          <p className="products-page-section-title">{t("my-selection")}</p>
        </div>
        <div className="filter-wrapper">
          {/* Brand Filter */}
          <div className="filter-column">
            <p className="filter-label">{t("filter-label-brand")}</p>
            {recommendedData.brands && recommendedData.brands.length > 0 && (
              <ProductFilterDropdown
                selectedValue={selectedBrand}
                onUpdateValue={(selectedValue) =>
                  setSelectedBrand(selectedValue)
                }
                allValues={["All-Brands", ...recommendedData.brands]}
                side="left"
              />
            )}
          </div>
          {/* Category Filter */}
          <div className="filter-column">
            <p className="filter-label">{t("filter-label-category")}</p>
            {recommendedData.categories &&
              recommendedData.categories.length > 0 && (
                <ProductFilterDropdown
                  selectedValue={selectedCategory}
                  onUpdateValue={(selectedValue) =>
                    setSelectedCategory(selectedValue)
                  }
                  allValues={["All-Categories", ...recommendedData.categories]}
                  side="right"
                />
              )}
          </div>
        </div>

        {/* Products */}
        <div className="products-wrapper" ref={productsRef}>
          {paginatedProducts.length > 0 ? (
            <ProductCards
              products={paginatedProducts}
              onShowMoreClick={() => {}}
            />
          ) : (
            <div className="not-found-container">
              <h4 className="not-found-header">{t("not-found-header")}</h4>
              <p className="not-found-text">{t("not-found-text")}</p>
            </div>
          )}
        </div>

        {filteredProducts.length > ProductsPerPage && (
          <div className="pagination">
            <button
              className="page-btn nav"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <IoChevronBackOutline />
            </button>
            {getPageNumbers().map((page, index) =>
              page === "…" ? (
                <span key={`ellipsis-${index}`} className="page-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={page}
                  className={`page-btn ${page === currentPage ? "active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}
            <button
              className="page-btn nav"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <IoChevronForwardOutline />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
